import React, { useState, useEffect, useRef } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import './TagSelector.css';

const DEFAULT_COLOR = '#62794e';

export default function TagSelector({ selected = [], onChange, readOnly = false }) {
  const userId = localStorage.getItem('userId');
  const [available, setAvailable] = useState([]);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    setFetchFailed(false);
    fetch(`${API_BASE}/api/tags/${encodeURIComponent(userId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => setAvailable(Array.isArray(data) ? data : []))
      .catch(() => setFetchFailed(true));
  }, [userId]);

  const unselected = available.filter((t) => !selected.includes(t.name));
  const suggestions = unselected.filter((t) =>
    t.name.toLowerCase().includes(inputValue.trim().toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function remove(name) {
    onChange(selected.filter((t) => t !== name));
  }

  function colorFor(name) {
    return available.find((t) => t.name === name)?.color || DEFAULT_COLOR;
  }

  async function commitTag(rawName) {
    const name = rawName.trim();
    if (!name || creating) return;

    const existing = available.find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selected.includes(existing.name)) onChange([...selected, existing.name]);
      setInputValue('');
      return;
    }

    if (!userId) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, name, color: DEFAULT_COLOR }),
      });
      if (!res.ok && res.status !== 409) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to create tag');
      }
      setAvailable((prev) =>
        prev.some((t) => t.name.toLowerCase() === name.toLowerCase())
          ? prev
          : [...prev, { userId, name, color: DEFAULT_COLOR }].sort((a, b) =>
              a.name.localeCompare(b.name)
            )
      );
      onChange([...selected, name]);
      setInputValue('');
      setOpen(false);
    } catch (err) {
      notify.error(err.message || 'Failed to create tag');
    } finally {
      setCreating(false);
    }
  }

  function selectSuggestion(name) {
    commitTag(name);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTag(inputValue);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="tag-selector" ref={containerRef}>
      {fetchFailed && !readOnly && (
        <span className="tag-selector-empty" style={{ color: '#dc2626' }}>
          Could not load tags — check your connection and refresh.
        </span>
      )}

      {!readOnly && !fetchFailed && (
        <div className="tag-selector-input-wrap">
          <input
            className="tag-selector-dropdown"
            value={inputValue}
            disabled={creating}
            autoComplete="off"
            placeholder={creating ? 'Creating…' : 'Add or create a tag…'}
            onChange={(e) => {
              setInputValue(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />
          {open && suggestions.length > 0 && (
            <ul className="tag-selector-suggestions">
              {suggestions.map((t) => (
                <li key={t.name}>
                  <button
                    type="button"
                    className="tag-selector-suggestion"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectSuggestion(t.name);
                    }}
                  >
                    <span
                      className="tag-selector-suggestion-swatch"
                      style={{ background: t.color || DEFAULT_COLOR }}
                    />
                    {t.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className="tag-selector-chips">
          {selected.map((name) => (
            <span
              key={name}
              className="tag-selector-chip"
              style={{ borderLeftColor: colorFor(name) }}
            >
              {name}
              {!readOnly && (
                <button
                  type="button"
                  className="tag-selector-remove"
                  onClick={() => remove(name)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {readOnly && selected.length === 0 && (
        <span className="tag-selector-empty">No tags</span>
      )}
    </div>
  );
}
